import { defineStore } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { useDicomUploadLifecycle } from '@/views/case-settings/upload/shared/useDicomUploadLifecycle';
import { createHipSpineStudy } from '@/views/case-settings/api/createHipSpineStudy';
import { useCaseSettings } from '@/stores/caseSettings/store';
import { useCaseSyncStore } from '@/stores/useCaseSyncStore';
import type { CaseStudy } from '@/stores/caseSettings/types';
import type { XRayFile } from '@/views/case-settings/upload/xray/createXRayStudy';

type CaseSettings = ReturnType<typeof useCaseSettings>;
type HipSpineLifecycle = ReturnType<typeof createHipSpineLifecycle>;
type HipSpineFileSlots = ReturnType<typeof useHipSpineFileSlots>;

interface HipSpineUploadDeps {
    lifecycle: HipSpineLifecycle;
    slots: HipSpineFileSlots;
    caseSettings: CaseSettings;
    caseSync: ReturnType<typeof useCaseSyncStore>;
}

/** The hip-spine flow's own instance of the shared DICOM upload lifecycle. */
function createHipSpineLifecycle(caseSettings: CaseSettings) {
    return useDicomUploadLifecycle<XRayFile>({
        modality: 'xray',
        requireSingleSeries: false,
        uploadType: 'xray-hip-spine',
        extractFile: (xf) => xf.file,
        createStudy: async ({ caseId }) => createHipSpineStudy(caseId),
        buildWorkerPayload: ({ inputs }) => inputs.map((xf) => ({ file: xf.file, view: xf.view })),
        onUploadComplete: ({ inputs }) => {
            caseSettings.state.case.study = {
                name: 'Hip spine xray files upload',
                series: 'hip-spine',
                fileCount: inputs.length,
                sourceModality: 'ct-scan',
            };
        },
        messages: {
            autoUpload: 'Hip spine x-ray files auto-uploaded',
            userConfirmed: 'Hip spine x-ray files uploaded by user',
            uploadDespiteMismatch: 'Hip spine x-ray files uploaded despite patient data mismatch',
        },
    });
}

/**
 * VM-owned file slots plus the `lastUploaded` record. `lastUploaded` is written
 * only by `recordUploaded` (called after a successful upload) and cleared by
 * `clear` (called by the ViewModel's reset); it is exposed for observability.
 */
function useHipSpineFileSlots() {
    const standingSagittal = ref<File | null>(null);
    const seatedSagittal = ref<File | null>(null);
    const lumbarSagittal = ref<File | null>(null);

    const lastUploaded = reactive({
        standingSagittal: null as File | null,
        seatedSagittal: null as File | null,
        lumbarSagittal: null as File | null,
    });

    function clear() {
        standingSagittal.value = null;
        seatedSagittal.value = null;
        lumbarSagittal.value = null;
        lastUploaded.standingSagittal = null;
        lastUploaded.seatedSagittal = null;
        lastUploaded.lumbarSagittal = null;
    }

    function recordUploaded() {
        lastUploaded.standingSagittal = standingSagittal.value;
        lastUploaded.seatedSagittal = seatedSagittal.value;
        lastUploaded.lumbarSagittal = lumbarSagittal.value;
    }

    return {
        standingSagittal,
        seatedSagittal,
        lumbarSagittal,
        lastUploaded,
        setStandingSagittal: (file: File) => {
            standingSagittal.value = file;
        },
        setSeatedSagittal: (file: File) => {
            seatedSagittal.value = file;
        },
        setLumbarSagittal: (file: File) => {
            lumbarSagittal.value = file;
        },
        clear,
        recordUploaded,
    };
}

/** Presentation state derived from the lifecycle, file slots, and case stores. */
function useHipSpineUploadDerivations({
    lifecycle,
    slots,
    caseSettings,
    caseSync,
}: HipSpineUploadDeps) {
    const hasFilesChanged = computed(() => {
        const hasPriorUpload =
            !!slots.lastUploaded.standingSagittal ||
            !!slots.lastUploaded.seatedSagittal ||
            !!slots.lastUploaded.lumbarSagittal;
        if (!hasPriorUpload) {
            return true;
        }
        return (
            fileNameDiffers(slots.standingSagittal.value, slots.lastUploaded.standingSagittal) ||
            fileNameDiffers(slots.seatedSagittal.value, slots.lastUploaded.seatedSagittal) ||
            fileNameDiffers(slots.lumbarSagittal.value, slots.lastUploaded.lumbarSagittal)
        );
    });

    const canStartProcessing = computed(
        () =>
            !!caseSync.study &&
            !!slots.standingSagittal.value &&
            (!lifecycle.hasUploaded.value || hasFilesChanged.value),
    );

    const getStudy = computed((): CaseStudy | null => caseSettings.case.study);

    return {
        hasFilesChanged,
        canStartProcessing,
        getStudy,
        hasScan: computed(() => lifecycle.hasUploaded.value || !!getStudy.value),
        hasEnteredPatientData: computed(() => caseSettings.hasEnteredPatientData),
        isCaseDeemedUnsuitable: computed(() => caseSettings.isCaseDeemedUnsuitable),
    };
}

/** The upload action: validate, run the lifecycle, then record what was uploaded. */
function useHipSpineUploadActions({
    lifecycle,
    slots,
    caseSettings,
}: Omit<HipSpineUploadDeps, 'caseSync'>) {
    async function uploadAndStartProcessing() {
        if (!slots.standingSagittal.value)
            throw new Error('The required standing sagittal file is missing');
        if (caseSettings.case.id == null) throw new Error('Case-id is null on upload');

        const xrays: XRayFile[] = [
            { view: 'standing-sagittal', file: slots.standingSagittal.value },
        ];
        if (slots.seatedSagittal.value)
            xrays.push({ view: 'flexed-seated-sagittal', file: slots.seatedSagittal.value });
        if (slots.lumbarSagittal.value)
            xrays.push({ view: 'lumbar-sagittal', file: slots.lumbarSagittal.value });

        await lifecycle.start(xrays);
        slots.recordUploaded();
    }

    return { uploadAndStartProcessing };
}

/** The curated lifecycle surface the View consumes (renames `inputs` → `xrayFiles`). */
function lifecycleSurface(lifecycle: HipSpineLifecycle) {
    return {
        state: lifecycle.state,
        error: lifecycle.error,
        hasUploaded: lifecycle.hasUploaded,
        progress: lifecycle.progress,
        patientIdentityCheck: lifecycle.patientIdentityCheck,
        patientComparison: lifecycle.patientComparison,
        processDicomsResult: lifecycle.processDicomsResult,
        isUploading: lifecycle.isUploading,
        isWaitingForConfirmation: lifecycle.isWaitingForConfirmation,
        isError: lifecycle.isError,
        isModalError: lifecycle.isModalError,
        isPatientDicomMismatch: lifecycle.isPatientDicomMismatch,
        hasMultiplePatientIdentities: lifecycle.hasMultiplePatientIdentities,
        patient: lifecycle.patient,
        uploadAnyway: lifecycle.uploadAnyway,
        userConfirmationUpload: lifecycle.userConfirmationUpload,
        cancel: lifecycle.cancel,
        xrayFiles: lifecycle.inputs,
    };
}

export const useHipSpineUploadViewModel = defineStore('hip-spine-upload-view', () => {
    const caseSettings = useCaseSettings();
    const caseSync = useCaseSyncStore();
    const lifecycle = createHipSpineLifecycle(caseSettings);
    const slots = useHipSpineFileSlots();
    const derivations = useHipSpineUploadDerivations({ lifecycle, slots, caseSettings, caseSync });
    const { uploadAndStartProcessing } = useHipSpineUploadActions({
        lifecycle,
        slots,
        caseSettings,
    });

    // Complete reset: clears the shared lifecycle context AND the VM-owned file
    // slots / lastUploaded record. `lifecycle.reset` alone leaves the latter two
    // stale, which is what leaked the success banner / Start-Processing gate
    // across cases.
    function reset() {
        lifecycle.reset();
        slots.clear();
    }

    // This ViewModel is a Pinia singleton whose state outlives navigation, so it
    // must self-reset on case-identity change or stale upload state leaks into
    // the next case. Watch the raw identity directly (not a derived load flag);
    // see docs/mvvm-migration.md "Resetting state on resource-identity change".
    watch(
        () => caseSettings.case.id,
        () => reset(),
    );

    return {
        ...lifecycleSurface(lifecycle),
        $reset: reset,
        standingSagittal: slots.standingSagittal,
        seatedSagittal: slots.seatedSagittal,
        lumbarSagittal: slots.lumbarSagittal,
        lastUploaded: slots.lastUploaded,
        setStandingSagittal: slots.setStandingSagittal,
        setSeatedSagittal: slots.setSeatedSagittal,
        setLumbarSagittal: slots.setLumbarSagittal,
        ...derivations,
        uploadAndStartProcessing,
    };
});

/** Two file slots differ when their names differ (a null slot has no name). */
function fileNameDiffers(current: File | null, uploaded: File | null): boolean {
    return current?.name !== uploaded?.name;
}
